/**
 * Geolocation Provider Factory
 * 
 * Creates the appropriate geolocation provider based on configuration.
 * Uses the VITE_GEO_PROVIDER environment variable to select the provider.
 */

import type { IGeoProvider } from './IGeoProvider';
import { MaxMindProvider } from './MaxMindProvider';
import { IpApiProvider } from './IpApiProvider';
import type { GeoProviderType } from '../../config/geolocation';
import { GEO_PROVIDER } from '../../config/geolocation';
import { createLogger } from '../../utils/logger';
const log = createLogger('GeoProviderFactory');

// Singleton instance
let providerInstance: IGeoProvider | null = null;

/**
 * Get the configured geolocation provider
 * Creates a singleton instance on first call
 * 
 * @returns The configured IGeoProvider implementation
 */
export function getGeoProvider(): IGeoProvider {
    if (!providerInstance) {
        providerInstance = createGeoProvider(GEO_PROVIDER);
        log.log(`Using provider: ${providerInstance.name}`);
    }
    return providerInstance;
}

/**
 * Create a geolocation provider by type
 * 
 * @param type - The provider type to create
 * @returns A new IGeoProvider instance
 */
export function createGeoProvider(type: GeoProviderType): IGeoProvider {
    // ip-api.com free tier does not support HTTPS.
    // If we are on HTTPS and ip-api is requested, fallback to MaxMind to avoid Mixed Content errors.
    if (type === 'ip-api' && typeof window !== 'undefined' && window.location.protocol === 'https:') {
        log.warn('"ip-api" provider does not support HTTPS. Falling back to "maxmind" provider.');
        return new MaxMindProvider();
    }

    switch (type) {
        case 'ip-api':
            return new IpApiProvider();
        case 'maxmind':
        default:
            return new MaxMindProvider();
    }
}

/**
 * Reset the singleton provider instance
 * Useful for testing or runtime provider switching
 */
export function resetGeoProvider(): void {
    providerInstance = null;
}
